/**
 * Recipe-detail screen state.
 *
 * `recipe-detail/[id]` is registered as a single hidden Tabs screen
 * (src/navigation/tabConfig.ts), so ONE component instance serves every recipe:
 * opening recipe B re-renders that instance rather than mounting a new one.
 * Loads, scrapes and translations started for recipe A are therefore still in
 * flight when B opens, and their results used to land on B — the user tapped one
 * recipe in search and got the one they had viewed before (#565).
 *
 * The fix lives in one line at the top of the reducer: every action produced by
 * an async job carries the `requestId` it was started for, and any action whose
 * `requestId` is no longer the recipe on screen is dropped. Keeping it here
 * rather than as a check at each call site means a new async path cannot
 * reintroduce the bug by forgetting to guard itself.
 */

import type { Recipe } from '../types/recipe';
import type { ScrapeErrorType } from '../components/recipe-detail/RecipeDataErrorCard';
import { hasRecipeContent } from '../utils/recipeSource';

export type ScrapeStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * Whether the recipe has settled with renderable content.
 * `unavailable` is the terminal "we tried everything and there is nothing to
 * show" state — it drives the error card instead of a bare empty section.
 */
export type ContentStatus = 'pending' | 'ready' | 'unavailable';

/** Why the page has nothing to render. The screen turns these into copy. */
export type RecipeLoadError = 'not-found' | 'load-failed';

export interface RecipeDetailState {
  /** The recipe the screen is currently showing. Every guard compares to this. */
  activeId?: string;
  recipe: Recipe | null;
  loading: boolean;
  error: RecipeLoadError | null;
  contentStatus: ContentStatus;
  scrapeStatus: ScrapeStatus;
  scrapeErrorType: ScrapeErrorType | null;
  isTranslating: boolean;
  showOriginal: boolean;
  /**
   * Pre-translation copy, kept so "show original" can restore it and so the
   * editor saves the recipe's own text (`editableRecipe`). Null whenever the
   * recipe on screen is not a translation of it.
   */
  original: Recipe | null;
  /**
   * The recipe on screen is the nav-param first paint, not the recipe: list
   * index fields with no steps and no summary. It is shown, never translated —
   * a translation of it is a stepless translation of the real recipe (#715).
   */
  isPlaceholder: boolean;
}

/**
 * Actions carrying a `requestId` were produced by an async job started for that
 * recipe, and are dropped once the user has moved on. Actions without one are
 * caused directly by the user and always apply.
 */
export type RecipeDetailAction =
  | { type: 'navigate'; id: string; initialRecipe: Recipe | null }
  | { type: 'loaded'; requestId: string; recipe: Recipe }
  | { type: 'loadFailed'; requestId: string; error: RecipeLoadError }
  | { type: 'contentUnavailable'; requestId: string }
  | { type: 'scrapeStarted'; requestId: string }
  | { type: 'scrapeSucceeded'; requestId: string; recipe: Recipe }
  | { type: 'scrapeFailed'; requestId: string; errorType: ScrapeErrorType }
  | { type: 'translateStarted'; requestId: string }
  | { type: 'translateSucceeded'; requestId: string; recipe: Recipe; original: Recipe }
  | { type: 'translateSettled'; requestId: string }
  | { type: 'localEdit'; updates: Partial<Recipe> }
  | { type: 'toggleOriginal' };

export const initialRecipeDetailState: RecipeDetailState = {
  activeId: undefined,
  recipe: null,
  loading: true,
  error: null,
  contentStatus: 'pending',
  scrapeStatus: 'idle',
  scrapeErrorType: null,
  isTranslating: false,
  showOriginal: false,
  original: null,
  isPlaceholder: false,
};

/** The state a freshly opened recipe starts from. */
export function stateForRecipe(id: string, initialRecipe: Recipe | null): RecipeDetailState {
  return {
    ...initialRecipeDetailState,
    activeId: id,
    recipe: initialRecipe,
    // Nav params give us a title, image and flat ingredient list to render
    // immediately, so there is nothing to spin on.
    loading: !initialRecipe,
    // Same test the screen uses to decide whether a background load follows.
    // One that already carries its content gets no `loaded`, so treating it as
    // a placeholder would leave it untranslated for good.
    isPlaceholder: !!initialRecipe && !hasRecipeContent(initialRecipe),
  };
}

/**
 * What opening a recipe has to fetch before the page is final.
 *
 * - `draft`: the Add Recipe sheet handed over the document it just wrote. It is
 *   the recipe, blank or not, so nothing is read and nothing is scraped (#791:
 *   reading it back held a Safari cook on the spinner for 30 s).
 * - `embedded`: the nav param already carries ingredients and steps.
 * - `background-load`: the nav param is a first paint; the rest loads under it.
 * - `load`: nothing to draw yet, so a full load behind the spinner.
 */
export type RecipeOpenPlan = 'draft' | 'embedded' | 'background-load' | 'load';

export function planRecipeOpen(initialRecipe: Recipe | null, handedOffDraft: Recipe | null): RecipeOpenPlan {
  if (handedOffDraft) return 'draft';
  if (!initialRecipe) return 'load';
  return hasRecipeContent(initialRecipe) ? 'embedded' : 'background-load';
}

/**
 * Whether a bundled recipe file is drawn before Firestore answers (#874).
 *
 * The file is the catalogue's text, and the bundle holds only catalogue
 * recipes. It used to wait for the Firestore read that could amend it, three
 * round trips behind, so a recipe already on the phone was the slowest to open
 * (Recipes tab, bundled: p50 2.9 s). A household's own copy keeps the
 * catalogue's id, though, and an edited copy drawn as the catalogue's would
 * change under the cook's eyes a moment later. The recipe index says which:
 * an entry that is not `base` is the household's copy, so that recipe waits as
 * before. No entry is a recipe the household has not saved.
 */
export function drawsBundledFileFirst(indexSource: string | undefined): boolean {
  return indexSource === undefined || indexSource === 'base';
}

/**
 * Whether two copies of a recipe would draw the same page. Firestore usually
 * adds nothing to a bundled file, and a second `loaded` would restart the
 * translation for nothing.
 */
export function sameRecipe(a: Recipe, b: Recipe): boolean {
  if (a === b) return true;
  const left = a as unknown as Record<string, unknown>;
  const right = b as unknown as Record<string, unknown>;
  for (const key of new Set([...Object.keys(left), ...Object.keys(right)])) {
    const x = left[key];
    const y = right[key];
    if (x === y || (x == null && y == null)) continue;
    if (typeof x !== 'object' || typeof y !== 'object' || x === null || y === null) return false;
    if (JSON.stringify(x) !== JSON.stringify(y)) return false;
  }
  return true;
}

/**
 * What an ingredients or steps section shows when it has nothing to list.
 *
 * `unavailable` is the error card, which blames the page (« La page s'est
 * chargée mais… ») and offers a Retry. A recipe with no source has no page:
 * a cook who wrote it by hand and left a section blank got that card, and a
 * Retry that could only load the same blank. Without a source it is `empty`,
 * the plain « Aucun ingrédient indiqué » with « Modifier la recette ».
 */
export type MissingContentView = 'skeleton' | 'scrape-error' | 'unavailable' | 'empty';

export function missingContentView(state: {
  scrapeStatus: ScrapeStatus;
  scrapeErrorType: ScrapeErrorType | null;
  contentStatus: ContentStatus;
  sourceUrl?: string | null;
}): MissingContentView {
  if (state.scrapeStatus === 'loading') return 'skeleton';
  if (state.scrapeStatus === 'error' && state.scrapeErrorType) return 'scrape-error';
  if (state.contentStatus === 'unavailable' && !!state.sourceUrl?.trim()) return 'unavailable';
  return 'empty';
}

export function recipeDetailReducer(
  state: RecipeDetailState,
  action: RecipeDetailAction,
): RecipeDetailState {
  // The whole of #565, in one place: a result belonging to a recipe the user is
  // no longer looking at never reaches the screen.
  if ('requestId' in action && action.requestId !== state.activeId) {
    return state;
  }

  switch (action.type) {
    case 'navigate':
      return stateForRecipe(action.id, action.initialRecipe);

    // Fresh content is not a translation; the translation effect re-runs for it.
    case 'loaded':
      return {
        ...state,
        recipe: action.recipe,
        original: null,
        isPlaceholder: false,
        loading: false,
        error: null,
        contentStatus: hasRenderableContent(action.recipe) ? 'ready' : state.contentStatus,
      };

    case 'loadFailed':
      return { ...state, loading: false, error: action.error, contentStatus: 'unavailable' };

    case 'contentUnavailable':
      return { ...state, loading: false, contentStatus: 'unavailable' };

    case 'scrapeStarted':
      return { ...state, scrapeStatus: 'loading', scrapeErrorType: null };

    case 'scrapeSucceeded':
      return {
        ...state,
        recipe: action.recipe,
        original: null,
        isPlaceholder: false,
        scrapeStatus: 'success',
        scrapeErrorType: null,
        contentStatus: hasRenderableContent(action.recipe) ? 'ready' : 'unavailable',
      };

    case 'scrapeFailed':
      return {
        ...state,
        scrapeStatus: 'error',
        scrapeErrorType: action.errorType,
        contentStatus: 'unavailable',
      };

    // Only announces the wait. A device-cache hit resolves too fast to be worth
    // announcing, and skips straight to translateSucceeded.
    case 'translateStarted':
      return { ...state, isTranslating: true };

    // Carries the pre-translation copy so it is captured with the swap rather
    // than at the start of one — the two are not the same event once a cache
    // hit can resolve without ever announcing a wait.
    //
    // A translation only replaces the recipe it was made from. One that resolves
    // after `loaded` or a scrape swapped the content is a translation of
    // something no longer on screen (#715: the placeholder's, with no steps).
    // The effect re-runs for the new content, so dropping it loses nothing.
    case 'translateSucceeded':
      if (action.original !== state.recipe) return state;
      return { ...state, recipe: action.recipe, original: action.original };

    case 'translateSettled':
      return { ...state, isTranslating: false };

    case 'localEdit':
      return state.recipe
        ? {
            ...state,
            recipe: { ...state.recipe, ...action.updates },
            original: state.original ? { ...state.original, ...action.updates } : null,
          }
        : state;

    case 'toggleOriginal':
      // Going back to the original swaps the translated copy out; going the
      // other way is a no-op on the recipe — the translation effect re-runs.
      return {
        ...state,
        showOriginal: !state.showOriginal,
        recipe: !state.showOriginal && state.original ? state.original : state.recipe,
      };

    default:
      return state;
  }
}

/**
 * The recipe as stored, for the editor. The screen may be showing a
 * translation, and editing that copy saved the translated title and lines over
 * the recipe's own (#792).
 */
export function editableRecipe(state: RecipeDetailState): Recipe | null {
  return state.original ?? state.recipe;
}

/** Ingredients or steps — the two things the page exists to show. */
function hasRenderableContent(recipe: Recipe | null): boolean {
  if (!recipe) return false;
  return (
    (recipe.extendedIngredients?.length ?? 0) > 0 ||
    (recipe.instructions?.length ?? 0) > 0
  );
}

/**
 * Whether the screen should translate what it is currently showing.
 *
 * A function of the recipe ON SCREEN and nothing else, on purpose. The screen
 * used to consult a persisted "this device already translated this recipe"
 * stamp and skip when it was set (#373). That stamp cannot distinguish
 * "the recipe in front of me is translated" from "I translated this recipe
 * last week" — and the translation is never stored on the recipe, only in the
 * translation caches, so a reload always brings it back in its ORIGINAL
 * language. Skipping therefore meant never applying, and an English recipe sat
 * in a French app permanently once it had been translated once.
 *
 * Re-running is cheap: `translateRecipe` answers from the device cache, then
 * Firestore, and only pays for the model on a true first translation.
 */
export function shouldTranslateRecipe(state: RecipeDetailState): boolean {
  // Not gated on `isTranslating`. The effect only re-runs when the recipe on
  // screen changes, and its cleanup cancels the run in flight — so a translation
  // already running is a translation of content that is gone. Waiting for it
  // left a recipe swapped in mid-translation (a scrape heal) untranslated.
  if (state.showOriginal) return false;
  if (state.recipe?.isTranslated) return false;
  if (state.isPlaceholder) return false;
  return hasRenderableContent(state.recipe);
}

/**
 * Whether a failed `/api/recipes/scrape` response deserves to be raised as an
 * exception in error tracking.
 *
 * Only a server fault does. Every 4xx the endpoint returns is the scraper
 * REACHING a verdict and saying so: 400 for a URL it won't touch, 401/403 for
 * auth, 422 for a bot wall, a login wall, or a page carrying no
 * machine-readable recipe at all (Le Devoir publishes its recipes as
 * `ReportageNewsArticle` news articles). The API picks 422 precisely to keep
 * those out of the 5xx budget — "client-actionable, not a server fault" — and
 * the client contradicting that paged a human for a designed-for outcome,
 * burying the failures that do need someone to look. The
 * `recipe_scrape_completed` analytic still records every failure with its
 * reason, so nothing is lost by staying quiet here.
 */
/**
 * What a failed fetch from the recipe's source tells the cook.
 *
 * A 401 is the scraper refusing a guest (#775). It used to be filed with 402
 * and 403 as "this site blocks automated access", which sent a guest off to
 * copy the recipe by hand when creating an account would have loaded it.
 */
export function scrapeErrorTypeForStatus(status: number): ScrapeErrorType {
  if (status === 401) return 'sign-in';
  if (status === 402 || status === 403) return 'blocked';
  if (status === 422 || status === 404 || status === 400) return 'not-found';
  return 'network';
}

/**
 * The error type for a scrape that threw before or instead of answering.
 *
 * A revoked session (`SessionTokenError`, `reason: 'revoked'`) is the one case
 * that is not the network: the cook has to sign in again, and `fail` records no
 * backoff for it. Everything else — a transport failure, a token refresh that
 * never landed — is `network` (#778).
 */
export function scrapeErrorTypeForThrown(error: unknown): ScrapeErrorType {
  const { name, reason } = (error ?? {}) as { name?: unknown; reason?: unknown };
  if (name === 'SessionTokenError' && reason === 'revoked') return 'sign-in';
  return 'network';
}

export function isScrapeStatusWorthReporting(status: number): boolean {
  return status >= 500;
}
